/**
 * Account UI
 * DOM updates for account section. Extracted to avoid circular imports.
 */

/* global document */

import type { AuthSession } from "../../shared/auth/types";

/** Minimal entitlement for plan + button visibility. Uses normalized plan/status. */
export interface EntitlementForUI {
  plan: "free" | "pro";
  status?: string;
  stripe_customer_id?: string | null;
  current_period_end?: string | null;
}

export function refreshAccountUI(session: AuthSession | null): void {
  const signedOutEl = document.getElementById("account-signed-out");
  const signedInEl = document.getElementById("account-signed-in");
  const emailEl = document.getElementById("account-email");
  const userIdEl = document.getElementById("account-user-id");

  if (!signedOutEl || !signedInEl) return;

  if (session) {
    signedOutEl.style.display = "none";
    signedInEl.style.display = "";
    if (emailEl) emailEl.textContent = session.user.email;
    if (userIdEl) userIdEl.textContent = session.user.id;
  } else {
    signedOutEl.style.display = "";
    signedInEl.style.display = "none";
    if (emailEl) emailEl.textContent = "";
    if (userIdEl) userIdEl.textContent = "";
  }
}

/**
 * Update plan label and Upgrade / Manage Subscription button visibility from entitlement.
 * Call after getEntitlements() or when entitlement is unknown (pass null to show "—" and hide buttons).
 */
/** Map internal status to friendly label for display. */
function statusToLabel(status: string | undefined | null): string {
  if (status == null || status === "") return "—";
  const s = status.toLowerCase();
  if (s === "active" || s === "trialing" || s === "past_due" || s === "canceled" || s === "none" || s === "free" || s === "anonymous") {
    return s;
  }
  return status;
}

/** Format current_period_end (ISO string or null) for display. */
function formatPeriodEnd(value: string | undefined | null): string {
  if (value == null || value === "") return "—";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString();
  } catch {
    return "—";
  }
}

export function setEntitlementUI(entitlement: EntitlementForUI | null): void {
  const planEl = document.getElementById("account-plan");
  const statusEl = document.getElementById("account-status");
  const periodEndEl = document.getElementById("account-period-end");
  const upgradeMonthlyBtn = document.getElementById("upgrade-monthly-btn");
  const upgradeAnnualBtn = document.getElementById("upgrade-annual-btn");
  const manageBtn = document.getElementById("manage-subscription-btn");

  const plan = entitlement?.plan ?? null;
  if (planEl) {
    planEl.textContent = plan ? (plan === "pro" ? "Pro" : "Free") : "—";
  }
  if (statusEl) {
    statusEl.textContent = statusToLabel(entitlement?.status);
  }
  if (periodEndEl) {
    periodEndEl.textContent = formatPeriodEnd(entitlement?.current_period_end);
  }

  const showUpgrade = entitlement && plan !== "pro";
  if (upgradeMonthlyBtn) {
    upgradeMonthlyBtn.style.display = showUpgrade ? "" : "none";
  }
  if (upgradeAnnualBtn) {
    upgradeAnnualBtn.style.display = showUpgrade ? "" : "none";
  }

  if (manageBtn) {
    const showManage =
      entitlement &&
      (plan === "pro" ||
        (entitlement.stripe_customer_id != null && entitlement.stripe_customer_id.length > 0));
    manageBtn.style.display = showManage ? "" : "none";
  }
}

export function showOtpFallback(): void {
  const link = document.getElementById("otp-fallback-link");
  if (link) {
    link.style.display = "";
  }
}
